import { defineStore } from 'pinia';
import { useUser } from './user';
import { useApi } from '@/lib/api';
import { useGlobalStore } from '@/store/global-store-base';
import { tagOrderIndex, sortByTagOrder } from '@lib/tag-order';
import { computed, type ComputedRef } from 'vue';
import type { OrderTagCategoryDto, OrderTagDto } from '@/ApiClient';

export const useOrderTags = defineStore('order-tags', () => {
  const api = useApi();
  const globalStoreBase = useGlobalStore({
    load: api.orderTag_GetOrderTagCategories,
    dto: 'OrderTagCategoryDto',
    create: api.orderTag_AddOrderTagCategory,
    delete: api.orderTag_DeleteOrderTagCategory,
    update: api.orderTag_UpdateOrderTagCategory,
    signalR: 'OrderTagUpdate',
  });
  const { loaded } = globalStoreBase;

  const any: ComputedRef<boolean> = computed(() => {
    return Boolean(loaded.value.length && loaded.value.find((tagGroup) => tagGroup.tags && tagGroup.tags.length > 0));
  });

  const sorted: ComputedRef<OrderTagCategoryDto[]> = computed(() => {
    return loaded.value
      .sort((a, b) => a.name?.localeCompare(b.name || '') || 0)
      .map((tagGroup) => ({
        ...tagGroup,
        tags: tagGroup.tags?.sort((a, b) => a.name?.localeCompare(b.name || '') || 0),
      }));
  });

  const flattened: ComputedRef<OrderTagDto[]> = computed(() => {
    return loaded.value.flatMap((tagGroup) => tagGroup.tags || []);
  });

  const baseCategory = computed(() => loaded.value[0]);

  async function addNewTag(name: string, color: string, tagCategoryId: number) {
    const brandId = useUser().brandId;

    const tagWithTagCategoryId = {
      name,
      color,
      tagCategoryId,
    };

    const tag = await api.orderTag_AddOrderTag(brandId, tagWithTagCategoryId);

    const categoryIndex = loaded.value.findIndex((category) => category.id === tagCategoryId);

    if (categoryIndex > -1) {
      if (!loaded.value[categoryIndex].tags) loaded.value[categoryIndex].tags = [];

      loaded.value[categoryIndex].tags?.push(tag);
    }

    return tag;
  }

  async function updateTag(tagId: number, name: string, color: string) {
    const brandId = useUser().brandId;

    const tag = await api.orderTag_UpdateOrderTag(brandId, tagId, { color, name });

    const categoryIndex = loaded.value.findIndex((category) => category.id === tag.tagCategoryId);
    if (categoryIndex > -1) {
      const tagIndexInCategory = loaded.value[categoryIndex].tags?.findIndex((tag) => tag.id === tagId);
      if (tagIndexInCategory !== undefined && tagIndexInCategory > -1) {
        loaded.value[categoryIndex].tags?.splice(tagIndexInCategory, 1, tag);
      }
    }

    return tag;
  }

  async function deleteTag(tagId: number) {
    const brandId = useUser().brandId;

    await api.orderTag_DeleteOrderTag(brandId, tagId);

    const category = loaded.value.find((c) => c.tags?.some((t) => t.id == tagId));
    if (category) {
      const index = category.tags!.findIndex((t) => t.id == tagId);
      category.tags!.splice(index, 1);
    }
  }

  function idsToTags(ids: number[]) {
    const tags = [];

    for (const id of ids) {
      const tag = getTagById(id);

      if (tag) tags.push(tag);
    }

    return tags;
  }

  function getTagById(id: number): OrderTagDto | undefined {
    return flattened.value.find((x) => x.id == id);
  }

  function getCategoryById(id: number): OrderTagCategoryDto | undefined {
    return loaded.value.find((x) => x.id == id);
  }

  // One palette-order Map per store: pill components sort tag ids through this instead of every
  // cell instance building its own Map over the whole palette.
  const orderIndexById: ComputedRef<Map<number, number>> = computed(() => tagOrderIndex(sorted.value));

  function sortByOrder<T extends { id: number }>(tags: T[]): T[] {
    return sortByTagOrder(tags, orderIndexById.value);
  }

  return {
    ...globalStoreBase,
    any,
    sorted,
    orderIndexById,
    sortByOrder,
    flattened,
    baseCategory,
    addNewCategory: globalStoreBase.create,
    updateCategory: globalStoreBase.update,
    deleteCategory: globalStoreBase.delete,
    addNewTag,
    updateTag,
    deleteTag,
    idsToTags,
    getTagById,
    getCategoryById,
  };
});
